import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import useDebouncedValue from 'hooks/useDebouncedValue';

import type { SearchableFormValues } from 'components/SearchableFilter';
import useSelectedFilterLabels from '../../../hooks/useSelectedFilterLabels';
import { useTeamsFiltersQuery } from '../useTeamsFiltersQuery';

interface UseTeamFiltersProps {
  value: string[];
  oemSegmentIds?: string[];
  onSubmit: (data: SearchableFormValues) => void;
}

export default function useTeamsFilters({
  value,
  oemSegmentIds = [],
  onSubmit,
}: UseTeamFiltersProps) {
  const [searchValue, setSearchString, debouncedSearchValue] =
    useDebouncedValue('');

  const {
    data: teamsData,
    loading,
    loadMore,
    loadingMore,
  } = useTeamsFiltersQuery({
    variables: {
      page: 1,
      text: debouncedSearchValue,
      oemSegmentIds,
    },
  });

  const onSearch = useCallback(
    (val: string) => {
      setSearchString(val);
    },
    [setSearchString],
  );

  const items = useMemo(
    () =>
      (teamsData?.managedSentinelTeams.collection ?? []).map((record) => ({
        id: record.id,
        label: record.name,
        value: record.id,
      })),
    [teamsData],
  );

  const labelsById = useSelectedFilterLabels(items);

  // When a single team is pre-selected but is not part of the currently loaded
  // results (e.g. it lives on a later page), fetch it by id so the filter
  // button can show its name instead of the generic fallback.
  const selectedId = value.length === 1 ? value[0] : undefined;

  const { data: selectedData } = useTeamsFiltersQuery({
    variables: {
      page: 1,
      text: '',
      ids: selectedId ? [selectedId] : [],
      oemSegmentIds: [],
    },
    skip:
      !selectedId ||
      !teamsData ||
      items.some((item) => item.value === selectedId) ||
      Boolean(labelsById[selectedId]),
  });

  const selectedLabel = selectedData?.managedSentinelTeams.collection.find(
    (record) => record.id === selectedId,
  )?.name;

  const text = useMemo(() => {
    if (value.length === 0) return t`All Teams`;

    if (value.length === 1)
      return (
        labelsById[value[0]] ||
        items.find((item) => item.value === value[0])?.label ||
        selectedLabel ||
        t`Team`
      );

    return t`Teams (${value.length})`;
  }, [labelsById, items, selectedLabel, value]);

  return {
    items,
    text,
    loading,
    searchValue,
    debouncedSearchValue,
    onSearch,
    mode: 'async' as const,
    value,
    onSubmit,
    onLoadMore: loadMore,
    loadingMore,
  };
}
